import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { SETTABLE_STATUSES, StatusPill } from "./AdminShared";

export function AdminFullViewDialog({ fullView, fullLoading, onClose }) {
  return (
    <Dialog open={!!fullView} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="admin-full-view-dialog"
        className="max-w-3xl max-h-[85vh] overflow-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-heading">Full incident view</DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            This open was recorded to the incident audit trail.
          </DialogDescription>
        </DialogHeader>
        {fullLoading ? (
          <div className="py-12 text-center text-sm text-zinc-500">
            <Loader2 className="h-5 w-5 mx-auto animate-spin" />
          </div>
        ) : fullView?.data ? (
          <div className="space-y-4 text-sm">
            <div>
              <div className="cre-overline">Title</div>
              <div className="mt-1 text-slate-900 font-medium">
                {fullView.data.title}
              </div>
            </div>
            <div>
              <div className="cre-overline">Description</div>
              <div className="mt-1 text-slate-700 whitespace-pre-wrap">
                {fullView.data.description}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs font-mono">
              <div>
                <div className="cre-overline">Severity</div>
                <div className="mt-1 text-slate-800">{fullView.data.severity}</div>
              </div>
              <div>
                <div className="cre-overline">Status</div>
                <div className="mt-1">
                  <StatusPill status={fullView.data.status} />
                </div>
              </div>
              <div>
                <div className="cre-overline">Affected system</div>
                <div className="mt-1 text-slate-800">
                  {fullView.data.affected_system || "—"}
                </div>
              </div>
              <div>
                <div className="cre-overline">Domain</div>
                <div className="mt-1 text-slate-800">
                  {fullView.data.domain_classification?.label || "—"}
                </div>
              </div>
            </div>
            {fullView.data.root_cause_analysis ? (
              <div>
                <div className="cre-overline">Root cause (summary)</div>
                <div className="mt-1 text-slate-700 whitespace-pre-wrap text-xs">
                  {fullView.data.root_cause_analysis.summary || "(none captured)"}
                </div>
              </div>
            ) : null}
            {fullView.data.runbook ? (
              <div>
                <div className="cre-overline">Runbook steps</div>
                <ol className="mt-1 list-decimal list-inside space-y-0.5 text-xs text-slate-700">
                  {(fullView.data.runbook.steps || []).map((s, i) => (
                    <li key={i}>{s.title || s.action || "—"}</li>
                  ))}
                </ol>
              </div>
            ) : null}
            <div className="text-[10px] font-mono text-zinc-500 border-t border-slate-100 pt-2">
              audit trail: {(fullView.data.audit_trail || []).length} events ·
              feedback: {(fullView.data.feedback || []).length}
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export function AdminStatusDialog({ statusEdit, setStatusEdit, onSubmit }) {
  return (
    <Dialog open={!!statusEdit} onOpenChange={(v) => !v && setStatusEdit(null)}>
      <DialogContent data-testid="admin-status-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">Update incident status</DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Recorded to the audit trail. Use this to correct stale incidents so
            MTTR / support metrics are not skewed.
          </DialogDescription>
        </DialogHeader>
        {statusEdit ? (
          <div className="space-y-3 text-sm">
            <div className="text-xs text-slate-600 truncate">{statusEdit.title}</div>
            <div className="flex items-center gap-2 text-xs">
              <span className="cre-overline">Current</span>
              <StatusPill status={statusEdit.current} />
            </div>
            <div>
              <div className="cre-overline mb-1.5">New status</div>
              <Select
                value={statusEdit.next}
                onValueChange={(v) => setStatusEdit((s) => ({ ...s, next: v }))}
              >
                <SelectTrigger data-testid="admin-status-select" className="rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SETTABLE_STATUSES.map((s) => (
                    <SelectItem
                      key={s}
                      value={s}
                      data-testid={`admin-status-option-${s}`}
                    >
                      {s.replaceAll("_", " ")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="cre-overline mb-1.5">Reason (optional)</div>
              <Textarea
                rows={3}
                data-testid="admin-status-reason"
                value={statusEdit.reason}
                onChange={(e) =>
                  setStatusEdit((s) => ({ ...s, reason: e.target.value }))
                }
                placeholder="e.g. On-call closed out manually; admin cleanup for MTTR accuracy"
                className="rounded-sm"
              />
            </div>
            <div className="flex items-center justify-end gap-2 pt-1">
              <Button
                variant="outline"
                className="rounded-sm"
                onClick={() => setStatusEdit(null)}
                data-testid="admin-status-cancel"
              >
                Cancel
              </Button>
              <Button
                className="rounded-sm bg-sky-600 hover:bg-sky-500 text-white"
                onClick={onSubmit}
                data-testid="admin-status-confirm"
              >
                Update status
              </Button>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
